// Sentence Structure — word order, ser vs estar, questions, negation,
// prepositions, contractions. Ported from the legacy views.structure; uses
// the shared renderGuideSections helper.
import { renderGuideSections } from "../ui/guideSections.js";

// Hand-drawn flat SVG of the S–V–O flow — accurate and editable because it's
// vector code, not a generated image.
const WORD_ORDER_FIGURE = `
  <div class="bg-white border border-sage-100 rounded-2xl p-4 sm:p-5 mb-6">
    <p class="text-xs font-semibold uppercase tracking-wide text-sage-400 mb-2">Word order at a glance</p>
    <svg viewBox="0 0 660 230" class="w-full h-auto max-w-2xl mx-auto" role="img" aria-label="Spanish word order: Subject then Verb then Object">
      <defs>
        <marker id="wo-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0 0 L8 3 L0 6 z" fill="#94a3b8"></path>
        </marker>
      </defs>
      <rect x="30" y="28" width="160" height="58" rx="14" fill="#d1fae5"></rect>
      <text x="110" y="63" text-anchor="middle" font-weight="700" fill="#065f46" font-size="17">SUBJECT</text>
      <rect x="250" y="28" width="160" height="58" rx="14" fill="#fef3c7"></rect>
      <text x="330" y="63" text-anchor="middle" font-weight="700" fill="#92400e" font-size="17">VERB</text>
      <rect x="470" y="28" width="160" height="58" rx="14" fill="#e0f2fe"></rect>
      <text x="550" y="63" text-anchor="middle" font-weight="700" fill="#075985" font-size="17">OBJECT</text>
      <path d="M190 57 H250" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <path d="M410 57 H470" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <text x="110" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">María</text>
      <text x="330" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">come</text>
      <text x="550" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">manzanas</text>
      <text x="110" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">María</text>
      <text x="330" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">eats</text>
      <text x="550" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">apples</text>
      <text x="330" y="206" text-anchor="middle" fill="#6b7280" font-size="13">Default order is Subject → Verb → Object — the same as English.</text>
    </svg>
    <p class="text-sage-600 text-sm mt-2">Because the verb ending already shows <i>who</i> is acting, Spanish can shuffle parts for emphasis — but <b>S–V–O</b> is the safe default. (Adjectives, though, go <b>after</b> the noun: <i>una casa <b>blanca</b></i>.)</p>
  </div>`;

export function structureView(app) {
  renderGuideSections(
    app,
    "Sentence Structure",
    "How Spanish sentences are built — word order, the two verbs for \"to be\", questions and negation, prepositions, and a couple of tiny but essential contractions.",
    [
      {
        title: "1. The big picture — four shifts from English",
        body: `
          <p class="text-sage-700 mb-2">Good news for an English speaker: the basic order is the same — <b>subject → verb → object</b>. The differences come down to a short list, and most of them make Spanish <i>simpler</i>, not harder.</p>
          <ol class="text-sage-700 text-sm space-y-1 list-decimal pl-5">
            <li>The subject is usually <b>dropped</b> — the verb ending already says who.</li>
            <li>Adjectives usually go <b>after</b> the noun.</li>
            <li>There's <b>no "do/does"</b> helper for questions or negatives — the plain verb does the job.</li>
            <li>Questions are made by <b>intonation</b> (and ¿ ?), not by reordering words.</li>
          </ol>
          <p class="text-sage-700 text-sm mt-3"><b>Don't translate "do":</b> <i>¿Trabajas?</i> already means "Do you work?" — there's no word for the question/negative <i>do</i>. The verb alone asks: <i>¿Habla inglés?</i> = "Does she speak English?"</p>
        `,
      },
      {
        title: "2. Basic word order",
        body: `
          <p class="text-sage-700 mb-2">Default order is <b>Subject – Verb – Object</b>, but it's looser than in English because verb endings already show who's doing what.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>(Yo) como manzanas.</i> — I eat apples. (Subject often dropped.)</li>
            <li><i>Me gusta el café.</i> — Literally: "Coffee is pleasing to me." The grammatical subject (<i>el café</i>) comes after the verb.</li>
            <li>Adjectives go <b>after</b> the noun: <i>una casa <b>blanca</b></i>.</li>
            <li>Object pronouns go <b>before</b> conjugated verbs: <i>Lo veo.</i> — I see him/it.</li>
            <li>With infinitives or gerunds, pronouns can attach to the end: <i>quiero verlo</i>, <i>estoy mirándolo</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. Ser vs Estar — two verbs for \"to be\"",
        body: `
          <p class="text-sage-700 mb-2">Spanish splits English "to be" into two verbs:</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Use SER for…</th><th class="py-2 px-3">Use ESTAR for…</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Identity / profession (<i>soy médico</i>)</td><td class="py-2 px-3">Location (<i>estoy en casa</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Origin / nationality (<i>es de Perú</i>)</td><td class="py-2 px-3">Temporary states (<i>estoy cansado</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Permanent traits (<i>es alto</i>)</td><td class="py-2 px-3">Moods / feelings (<i>está feliz hoy</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Time / dates (<i>son las tres</i>)</td><td class="py-2 px-3">Ongoing actions (<i>está comiendo</i>)</td></tr>
              <tr><td class="py-2 pr-3">Material / possession (<i>es de madera</i>)</td><td class="py-2 px-3">Results of change (<i>está roto</i>)</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Meaning shifts:</b> <i>ser aburrido</i> = to be boring · <i>estar aburrido</i> = to be bored. <i>ser listo</i> = to be clever · <i>estar listo</i> = to be ready.</p>
        `,
      },
      {
        title: "4. Questions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Yes/no questions just flip intonation — no extra word needed. Written: <i>¿Hablas español?</i></li>
            <li>Always use <b>opening</b> punctuation: <i>¿…?</i> and <i>¡…!</i></li>
            <li><b>Question words always carry a written accent:</b> qué, quién, cuándo, dónde, cómo, cuál, cuánto, por qué.</li>
            <li>Examples: <i>¿Qué haces? ¿Dónde vives? ¿Cuántos años tienes? ¿Por qué no vienes?</i></li>
            <li><b>qué</b> vs <b>cuál</b>: use <i>qué</i> for definitions; <i>cuál</i> for choices. <i>¿Qué es esto?</i> vs <i>¿Cuál prefieres?</i></li>
          </ul>
        `,
      },
      {
        title: "5. Negation",
        body: `
          <p class="text-sage-700 mb-2">Put <b>no</b> directly before the verb: <i>No hablo francés.</i></p>
          <p class="text-sage-700 mb-2">Unlike English, Spanish uses <b>double negatives</b> — they don't cancel each other out:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>No tengo <b>nada</b>.</i> — I don't have anything.</li>
            <li><i>No conozco a <b>nadie</b>.</i> — I don't know anyone.</li>
            <li><i><b>Nunca</b> voy</i> OR <i>No voy <b>nunca</b></i> — I never go. (Either is fine.)</li>
            <li>If the negative word comes <b>before</b> the verb, drop the <i>no</i>; if it comes after, keep it.</li>
          </ul>
        `,
      },
      {
        title: "6. Common prepositions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a</b> — to, at; marks personal direct objects (<i>veo <b>a</b> María</i>).</li>
            <li><b>de</b> — of, from; possession (<i>el libro <b>de</b> Juan</i>).</li>
            <li><b>en</b> — in, on, at (location, not motion).</li>
            <li><b>con / sin</b> — with / without.</li>
            <li><b>por</b> — because of, by, through, in exchange for (cause/duration).</li>
            <li><b>para</b> — for, in order to, destined for (goal/recipient).</li>
            <li><b>desde / hasta</b> — from / until (time or space).</li>
            <li><b>entre</b> — between, among.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Personal "a":</b> when the direct object is a specific person, add <i>a</i>: <i>Busco <b>a</b> mi hermano</i> vs <i>Busco un libro</i> (no <i>a</i>).</p>
        `,
      },
      {
        title: "7. Contractions — only two!",
        body: `
          <p class="text-sage-700 mb-2">Spanish has just two mandatory contractions, both with <b>el</b>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a + el → al</b>: <i>Voy <b>al</b> mercado.</i> (not <i>a el mercado</i>)</li>
            <li><b>de + el → del</b>: <i>El coche <b>del</b> profesor.</i> (not <i>de el profesor</i>)</li>
          </ul>
          <p class="text-sage-700 text-sm mt-2">These do <b>not</b> happen with <i>la, los, las</i>, or with the pronoun <i>él</i> (with the accent): <i>el libro de él</i>, <i>a la casa</i>.</p>
        `,
      },
    ],
    WORD_ORDER_FIGURE
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
